import React, { useCallback, useEffect, useState } from 'react'
import * as Tabs from '@radix-ui/react-tabs'
import { Button } from '@radix-ui/themes'
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors, DragEndEvent } from '@dnd-kit/core'
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'

import { useMeliCategoryAttributes, useMeliTypes, useMunicipalities } from '@/hooks/useMeliDataSWR'
import useMeliPublisher from '@/hooks/useMeliPublisher'
import { useAuth } from '@/hooks/useAuth'
import { invertedCharacteristics } from '@/utils/characteristicTranslate'
import { BranchDto } from '@/interfaces/Branch'
import Vehicle from '@/interfaces/Vehicle'
import InputField from '@/components/InputField'
import { SelectField } from '@/components/SelectField'
import InputTextArea from '@/components/InputTextArea'
import useFileUpload, { UserOrganization } from '@/hooks/useFileUpload'
import useImagesReorder from '@/hooks/useImagesReorder'
import useImageSelection from '@/hooks/useImageSelection'
import useImageBulkDownload from '@/hooks/useImageBulkDownload'
import { makeSmallImage } from '@/utils/makeImage'
import VehicleVideoTab from '@/components/VehicleVideoTab'
import { Rule, validateFields } from '@/utils/validateForm'
import ConfirmationModal from '@/components/ConfirmationModal'
import ImageSelectionToolbar from '@/components/ImageSelectionToolbar'
import ImageLightbox from '@/components/ImageLightbox'
import ChileAutosForm from '@/components/ChileAutosForm'
import YapoForm from '@/components/YapoForm'
import usePublicationDataSaver from '@/hooks/usePublicationDataSaver'
import useLocalization from '@/hooks/useLocalization'
import useMeliPublishingCore from '@/hooks/useMeliPublishingCore'
import Region from '@/interfaces/Region'
import { autoSelectColor } from '@/utils/colorMappingHelpers'
import { Info } from '@/components/MessageInfo'

// Define a more specific type for the vehicle on this page
type VehicleWithNestedOrg = Vehicle & {
  organization: {
    _id: string
    name: string
    // include other organization properties if known
  }
}

const SortableItem = ({
  id,
  selectionMode = false,
  isSelected = false,
  onSelectionChange,
  onImageClick,
}: {
  id: string
  selectionMode?: boolean
  isSelected?: boolean
  onSelectionChange?: (id: string) => void
  onImageClick?: () => void
}) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id })
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    touchAction: 'none',
  }

  const handleImageClick = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    console.log('Image clicked, selectionMode:', selectionMode, 'onImageClick exists:', !!onImageClick)

    if (!selectionMode && onImageClick) {
      console.log('Calling onImageClick')
      onImageClick()
    }
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`mb-2 flex items-center justify-center rounded-md border bg-white p-2 shadow-sm relative ${
        selectionMode && isSelected ? 'ring-2 ring-blue-500' : ''
      }`}
    >
      {/* Drag handle - only visible when not in selection mode */}
      {!selectionMode && (
        <div
          {...attributes}
          {...listeners}
          className="absolute top-2 right-2 z-10 bg-gray-700/80 hover:bg-gray-600/80 text-white rounded p-1 cursor-grab active:cursor-grabbing"
          title="Arrastrar para reordenar"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <path d="M9 3h2v2H9zm0 4h2v2H9zm0 4h2v2H9zm0 4h2v2H9zm4-12h2v2h-2zm0 4h2v2h-2zm0 4h2v2h-2zm0 4h2v2h-2z" />
          </svg>
        </div>
      )}

      {/* Selection checkbox */}
      {selectionMode && (
        <div className="absolute top-2 left-2 z-10">
          <input
            type="checkbox"
            className="w-5 h-5 rounded border-gray-300 bg-white shadow-sm focus:ring-2 focus:ring-blue-500"
            checked={isSelected}
            onChange={() => onSelectionChange?.(id)}
          />
        </div>
      )}

      <img
        src={makeSmallImage(id)}
        alt="Vehicle"
        className="h-[256px] w-[247px] rounded-md object-cover cursor-pointer transition-opacity duration-200 hover:opacity-90"
        onClick={handleImageClick}
        title="Clic para ver en pantalla completa"
      />
    </div>
  )
}

interface PublicationFormManagerProps {
  vehicle: VehicleWithNestedOrg
  branch: BranchDto | null
  onPublishSuccess: () => void
  // NEW: Separate callback for image uploads (doesn't close form)
  onImageUpload?: (vehicleId: string) => void
  // NEW: Separate callback for video uploads (doesn't close form)
  onVideoUpload?: (vehicleId: string) => void
  // NEW: Props for multi-form state management
  formData?: PublicationFormData
  onFormDataChange?: (vehicleId: string, data: PublicationFormData) => void
  validationErrors?: Record<string, string>
  onValidationErrorsChange?: (vehicleId: string, errors: Record<string, string>) => void
}

interface PublicationFormData {
  [key: string]: string | number | undefined
  title?: string
  type?: string
  body?: string
  brand?: string
  model?: string
  version?: string
  fuel?: string
  gearbox?: string
  color?: string
  doors?: number
  commune?: string
  street?: string
  comments?: string
}

const PublicationFormManager: React.FC<PublicationFormManagerProps> = ({
  vehicle,
  branch,
  onPublishSuccess,
  onImageUpload,
  onVideoUpload,
  formData: externalFormData,
  onFormDataChange,
  validationErrors: externalValidationErrors,
  onValidationErrorsChange,
}) => {
  const [formData, setFormData] = useState<PublicationFormData>({})
  const [formErrors, setFormErrors] = useState<Record<string, string>>({})

  // Clean SWR data fetching - no infinite loops possible
  const { data: categoryAttrs } = useMeliCategoryAttributes()
  const { data: types } = useMeliTypes()
  const { data: municipalities } = useMunicipalities()

  const { meliPublish, meliSetPublished } = useMeliPublisher()
  const { userData } = useAuth()
  const { buildMeliPayload } = useMeliPublishingCore()

  // Add draft data saving functionality
  const {
    savePublicationData,
    loadPublicationData,
    loading: savingLoading,
    error: saveError,
    clearError,
  } = usePublicationDataSaver()

  // Add localization functionality (same as Meli.tsx)
  const { fetchMunicipalities, fetchRegionDetail } = useLocalization()

  const [publicImages, setPublicImages] = useState<string[]>([])
  const [hasUserUploadedImages, setHasUserUploadedImages] = useState<boolean>(false)
  const { imagesReorder } = useImagesReorder()
  const [isOrderSaveDisabled, setOrderSaveDisabled] = useState<boolean>(true)

  // Bulk download and delete functionality
  const { downloadMultipleImages, downloadProgress, deleteMultipleImages, deleteProgress } = useImageBulkDownload()

  // State for delete confirmation
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState<boolean>(false)
  const [imagesToDelete, setImagesToDelete] = useState<string[]>([])

  // Bulk delete handler with confirmation
  const handleBulkDeleteRequest = useCallback((imageUrls: string[]) => {
    setImagesToDelete(imageUrls)
    setDeleteConfirmationOpen(true)
  }, [])

  // Image selection functionality (defined before handlers that use it)
  const imageSelection = useImageSelection({
    images: publicImages,
    onBulkDownload: downloadMultipleImages,
    onBulkDelete: handleBulkDeleteRequest,
  })

  const handleBulkDeleteConfirm = useCallback(async () => {
    setDeleteConfirmationOpen(false)
    const result = await deleteMultipleImages(imagesToDelete, vehicle._id, false, (deletedImageUrl) => {
      // Update local state immediately for each successful deletion
      setPublicImages((prevImages) => prevImages.filter((url) => url !== deletedImageUrl))
    })

    if (result && result.success > 0) {
      // Clear selection after successful deletions
      imageSelection.resetSelection()

      // Trigger image upload callback to refresh parent state
      if (onImageUpload) {
        onImageUpload(vehicle._id)
      }
    }

    setImagesToDelete([])
  }, [imagesToDelete, deleteMultipleImages, vehicle._id, onImageUpload, imageSelection])

  const handleBulkDeleteCancel = useCallback(() => {
    setDeleteConfirmationOpen(false)
    setImagesToDelete([])
  }, [])

  // Lightbox functions
  const openImageInLightbox = (index: number) => {
    console.log('Opening lightbox with index:', index, 'Total images:', publicImages.length)
    setLightboxInitialIndex(index)
    setIsLightboxOpen(true)
  }

  const closeLightbox = () => {
    console.log('Closing lightbox')
    setIsLightboxOpen(false)
  }

  // Enhanced validation state
  const [confirmationModalOpen, setConfirmationModalOpen] = useState<boolean>(false)
  const [shortDescriptionModalOpen, setShortDescriptionModalOpen] = useState<boolean>(false)
  const MIN_DESCRIPTION_LENGTH = 55

  // NEW: Notification system (Level 2 - Standard)
  const [publishMessage, setPublishMessage] = useState<string>('')
  const [publishTrigger, setPublishTrigger] = useState<number>(0)
  const [isPublishing, setIsPublishing] = useState<boolean>(false)

  // Esta pantalla se abre sobre vehículos que en su mayoría ya están publicados,
  // y el botón de publicar no distinguía los dos casos: enviaba siempre al
  // endpoint de publicación, que al encontrar un identificador existente
  // republica y sobrescribe el aviso en línea. Se deriva del vehículo en cada
  // render para que no quede desfasado después de publicar. Se mira el
  // identificador además de la fecha porque es el identificador lo que hace que
  // la API republique en vez de crear.
  const isPublishedOnMeli = !!vehicle.external_meli?.identifier || !!vehicle.external_meli?.publishedAt

  // NEW: Platform selection state
  const [selectedPlatform, setSelectedPlatform] = useState<'mercadolibre' | 'chileautos' | 'yapo'>('mercadolibre')
  // NEW: Tab state for each platform (nested tabs within each platform)
  const [activeTab, setActiveTab] = useState<'details' | 'images' | 'video'>('details')

  // Lightbox state for enhanced image viewing
  const [isLightboxOpen, setIsLightboxOpen] = useState(false)
  const [lightboxInitialIndex, setLightboxInitialIndex] = useState(0)

  // Add draft loading state
  // Loading and message states for draft functionality (same as vehicle detail page)
  const [isDraftLoading, setIsDraftLoading] = useState<boolean>(false)
  const [minimumTimeElapsed, setMinimumTimeElapsed] = useState<boolean>(false)
  const [externalApisComplete, setExternalApisComplete] = useState<boolean>(false)
  const [saveMessage, setSaveMessage] = useState<string>('')

  // Add region state management (same as Meli.tsx)
  const [regions, setRegions] = useState<Region[]>([])
  const [branchRegionId, setBranchRegionId] = useState<string>('')

  const userOrganization: UserOrganization | null = userData
    ? {
        organization_id: vehicle.organization._id,
        user_id: userData?.user?._id ?? '',
        vehicle_id: vehicle._id,
        registry: '0',
        attachment: '0',
      }
    : null

  const { isUploading, handleFileChangeEx } = useFileUpload(userOrganization!, (newImageUrls?: string[]) => {
    if (newImageUrls && newImageUrls.length > 0) {
      setHasUserUploadedImages(true)
      setPublicImages((prevImages) => {
        const updatedImages = [...prevImages, ...newImageUrls]

        return updatedImages
      })

      // Call image upload callback to refresh data without closing form
      if (onImageUpload) {
        onImageUpload(vehicle._id)
      }
    }
  })

  // Helper functions from Meli.tsx
  const makeTitle = (vehicle: Vehicle) => {
    return `${vehicle.brand} ${vehicle.model} ${vehicle.version}`
  }

  const convertVehicleTypeToMeli = (type: string): string => {
    // Handle undefined or empty vehicle types - default to automobile for most vehicles
    if (!type || type === 'undefined' || type.trim() === '') {
      return 'MLC1744' // Default to Autos y camionetas for undefined types
    }

    switch (type) {
      case 'TYPES.VEHICLE.AUTOMOBILE':
        return 'MLC1744' // Autos y camionetas
      case 'TYPES.VEHICLE.MOTORCYCLE':
        return 'MLC1763' // Motos
      case 'TYPES.VEHICLE.NAUTIC':
        return 'MLC1785' // Náuticos
      default:
        return 'MLC1744' // Changed default from MLC1907 to MLC1744 since most vehicles are automobiles
    }
  }

  const validateDescription = (description: string): boolean => {
    return description.trim().length >= MIN_DESCRIPTION_LENGTH
  }

  // Add region change handler (same as Meli.tsx)
  const handleRegionChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { value } = e.target
    setBranchRegionId(value)

    // Find the region name from the regionId and save that to formData.commune
    const selectedRegion = regions.find((r) => r.regionId === value)
    const regionName = selectedRegion ? selectedRegion.name : value

    setFormData((prev) => ({ ...prev, commune: regionName }))
  }

  // Draft saving function (same pattern as Meli.tsx)
  const handleSaveDraft = async () => {
    setSaveMessage('')
    clearError()

    const result = await savePublicationData(vehicle._id, 'meli', formData as unknown as Record<string, unknown>)

    if (result.success) {
      setSaveMessage('✅ Borrador guardado correctamente')
      setTimeout(() => setSaveMessage(''), 3000)
    } else {
      setSaveMessage(`❌ Error: ${result.message}`)
      setTimeout(() => setSaveMessage(''), 5000)
    }
  }

  // Modified initialization effect to load regions first, then other data
  useEffect(() => {
    // First load regions data (same as Meli.tsx)
    const fetchRegionsData = async () => {
      const regs = await fetchMunicipalities()

      if (regs) {
        setRegions(regs)
      }
    }

    fetchRegionsData()
  }, [])

  // Main initialization effect with timing control (same as vehicle detail page)
  useEffect(() => {
    // Only initialize when all SWR data AND regions are loaded
    if (!categoryAttrs || !types || !municipalities || regions.length === 0) return

    const initializeComponentData = async () => {
      // Show loading message immediately and start minimum timer
      setIsDraftLoading(true)
      setTimeout(() => setMinimumTimeElapsed(true), 1500)

      // Step 1: Load draft data first to ensure it's available for merging.
      const draftResult = await loadPublicationData<PublicationFormData>(vehicle._id, 'meli')
      const draftData = draftResult.success && draftResult.data ? draftResult.data : {}

      // Step 2: Establish the base data from the vehicle object.
      const baseData = {
        title: makeTitle(vehicle),
        type: convertVehicleTypeToMeli(vehicle.type),
        body: vehicle.body_format,
        brand: vehicle.brand,
        model: vehicle.model,
        version: vehicle.version,
        fuel: vehicle.fuel_format,
        gearbox: vehicle.gearbox_format,
        color: vehicle.color,
        doors: vehicle.external?.meli?.attributes?.doors || 0,
        commune: branch?.municipality_format,
        street: '',
        // CRITICAL: Use promo.message as source, NOT vehicle.comments (internal field)
        comments: vehicle.promo?.message || ' ',
      }

      // Step 3: Merge base data with draft data (draft takes precedence).
      const mergedData = { ...baseData, ...draftData }

      // Step 4: NOW, perform the 55-character validation on the final merged comments.
      const orgDefaultDescription = userData?.organization?.external?.meli?.defaultDescription
      let finalComments = mergedData.comments

      if (!finalComments || String(finalComments).trim().length < 55) {
        finalComments = orgDefaultDescription || ''
      }

      // Step 5: Create the final form data object for state and notify the parent.
      const initialFormData: PublicationFormData = {
        ...mergedData,
        comments: finalComments,
      }

      setFormData(initialFormData)

      if (onFormDataChange) {
        onFormDataChange(vehicle._id, initialFormData)
      }

      // Auto-select the correct color based on vehicle color for MercadoLibre
      if (categoryAttrs.colors && categoryAttrs.colors.length > 0) {
        const colorSelection = autoSelectColor(vehicle.color, categoryAttrs.colors, 'mercadolibre')

        if (colorSelection.selectedColor) {
          setFormData((prev) => ({ ...prev, color: colorSelection.selectedColor! }))
        }
      }

      // Only initialize publicImages once, don't reset if user has uploaded images
      setPublicImages((prevImages) => {
        // If user has uploaded images, preserve them
        if (hasUserUploadedImages || prevImages.length > 0) return prevImages

        // Otherwise, initialize from vehicle data
        return vehicle.pictures?.public ?? []
      })

      setExternalApisComplete(true)
      setIsDraftLoading(false)
    }

    initializeComponentData()
  }, [categoryAttrs, types, municipalities, regions.length, vehicle._id]) // Proper dependencies

  // Branch auto-population effect (same pattern as Meli.tsx)
  useEffect(() => {
    if (!vehicle.branch || !branch || regions.length === 0) return

    // Auto-populate street address
    const firstToken = (branch.address ?? '').split(',')[0].trim()
    let street = `${firstToken} ${branch.streetNumber}`

    if (branch.addressComponents) {
      street = `${street} - ${branch.addressComponents}`
    }

    // Find matching region based on branch municipality (same as Meli.tsx)
    const comuna = branch?.municipality_format ?? ''
    const region = regions.find((r) => r.name === comuna)

    if (region) {
      setBranchRegionId(region.regionId)
    }

    setFormData((prev) => ({
      ...prev,
      street: street,
      commune: comuna, // Store the name, not the ID
    }))
  }, [vehicle.branch, branch?.municipality_format, regions.length]) // Use regions.length instead of regions

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    const updatedFormData = { ...formData, [name]: value }
    setFormData(updatedFormData)

    // NEW: Notify parent component of form data changes for persistence
    if (onFormDataChange) {
      onFormDataChange(vehicle._id, updatedFormData)
    }

    // NEW: Clear validation errors for this field when user starts typing
    if (formErrors[name]) {
      const updatedErrors = { ...formErrors }
      delete updatedErrors[name]
      setFormErrors(updatedErrors)

      if (onValidationErrorsChange) {
        onValidationErrorsChange(vehicle._id, updatedErrors)
      }
    }
  }

  const sensors = useSensors(useSensor(PointerSensor))

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event

    if (over && active.id !== over.id) {
      setPublicImages((items) => {
        const oldIndex = items.indexOf(active.id as string)
        const newIndex = items.indexOf(over.id as string)
        const newOrder = arrayMove(items, oldIndex, newIndex)
        setOrderSaveDisabled(false)

        return newOrder
      })
    }
  }

  const handleSaveOrder = async () => {
    const resp = await imagesReorder(publicImages, vehicle._id, false)

    if (resp) {
      setOrderSaveDisabled(true)

      // Trigger data refresh to get updated image order from database
      if (onImageUpload) {
        onImageUpload(vehicle._id)
      }
    }
  }

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (userOrganization) {
      await handleFileChangeEx(event)
    }
  }

  const handleMeliPublish = async () => {
    // Skip validation here since it was already done in beforePublish()

    if (!userData || !userData.organization) {
      setPublishMessage('❌ Error: Usuario no autenticado')
      setPublishTrigger(Date.now())

      return
    }

    if (!branch) {
      setPublishMessage('❌ Error: No se encontró la sucursal')
      setPublishTrigger(Date.now())

      return
    }

    const characteristics = (vehicle.characteristics || []).map((c) => invertedCharacteristics[c])

    // Get region detail using branchRegionId (same as Meli.tsx)
    const region = await fetchRegionDetail(branchRegionId)

    if (!region) {
      setPublishMessage('❌ Error: No se encontró la región para MercadoLibre')
      setPublishTrigger(Date.now())

      return
    }

    try {
      // NEW: Use shared core for consistent payload construction
      const publishingConfig = {
        vehicle,
        formData,
        userData,
        branch,
        publicImages,
        characteristics,
        region,
      }

      // CRITICAL FIX: Use shared core payload builder (ensures consistency with bulk publishing)
      const vehicleToPublish = buildMeliPayload(publishingConfig)

      const result = await meliPublish(vehicleToPublish, userData?.profile?._id ?? '')

      if (result?.ok) {
        // Success notification
        setPublishMessage('✅ Vehículo publicado exitosamente en MercadoLibre')
        setPublishTrigger(Date.now())

        if (userData?.profile) {
          await meliSetPublished(
            vehicle._id,
            userData?.profile?._id,
            result.inventoryIdentifier,
            result.permalink,
            result.title
          )
        }
        onPublishSuccess()
      } else {
        // API returned ok: false
        setPublishMessage('❌ Error al publicar en MercadoLibre')
        setPublishTrigger(Date.now())
      }
    } catch (error) {
      // Network or other errors
      setPublishMessage(`❌ Error al publicar: ${error instanceof Error ? error.message : 'Error desconocido'}`)
      setPublishTrigger(Date.now())
    }
  }

  // Validation function (same as Meli.tsx).
  // `skipDescriptionPrompt` se usa al revalidar después de que el usuario ya
  // aceptó una descripción corta: sin él, el modal se volvía a abrir en lugar de
  // publicar y no había forma de salir del ciclo.
  const validateFormData = ({ skipDescriptionPrompt = false } = {}): boolean => {
    // Comprehensive field validation
    const fieldsToValidate = [
      { name: 'type', value: formData.type ?? '', rule: Rule.REQUIRED },
      { name: 'body', value: formData.body ?? '', rule: Rule.REQUIRED },
      { name: 'brand', value: formData.brand ?? '', rule: Rule.REQUIRED },
      { name: 'model', value: formData.model ?? '', rule: Rule.REQUIRED },
      { name: 'fuel', value: formData.fuel ?? '', rule: Rule.REQUIRED },
      { name: 'gearbox', value: formData.gearbox ?? '', rule: Rule.REQUIRED },
      { name: 'color', value: formData.color ?? '', rule: Rule.REQUIRED },
      { name: 'comments', value: formData.comments ?? '', rule: Rule.REQUIRED },
      { name: 'title', value: formData.title ?? '', rule: Rule.REQUIRED },
      { name: 'street', value: formData.street ?? '', rule: Rule.REQUIRED },
    ]

    // Add doors validation for cars
    if (formData.type === 'MLC1744') {
      fieldsToValidate.push({
        name: 'doors',
        value: formData.doors?.toString() ?? '0',
        rule: Rule.GREATER_ZERO,
      })
    }

    const validationErrors = validateFields(fieldsToValidate)

    if (Object.keys(validationErrors).length > 0) {
      setFormErrors(validationErrors)

      return false
    }

    // Check description length - show modal if too short
    if (!skipDescriptionPrompt && !validateDescription(formData.comments ?? '')) {
      setShortDescriptionModalOpen(true)

      return false
    }

    // Check for public images
    if (!publicImages || publicImages.length === 0) {
      setFormErrors({ images: 'El vehículo debe tener al menos una imagen pública.' })

      return false
    }

    return true
  }

  // Guarda el borrador y luego publica. Lo comparten el camino directo (aviso
  // nuevo) y el que pasa por confirmación (aviso ya publicado).
  const saveDraftAndPublish = async () => {
    // Set loading state
    setIsPublishing(true)
    setPublishMessage('')

    try {
      // NEW: Save draft data first before publishing
      console.log('Saving draft data before publishing to MercadoLibre...')
      const saveResult = await savePublicationData(vehicle._id, 'meli', formData as unknown as Record<string, unknown>)

      if (!saveResult.success) {
        setPublishMessage(`❌ Error al guardar datos: ${saveResult.message}`)
        setPublishTrigger(Date.now())

        return
      }

      console.log('✅ Draft data saved successfully, proceeding with MercadoLibre publication')

      await handleMeliPublish()
    } finally {
      // Always reset loading state
      setIsPublishing(false)
    }
  }

  // Button click handler. Un aviso nuevo se publica directo; uno que ya está en
  // línea pide confirmación primero, porque republicar sobrescribe la publicación
  // existente en Mercado Libre.
  const beforePublish = async () => {
    if (!validateFormData()) {
      return
    }

    if (isPublishedOnMeli) {
      setConfirmationModalOpen(true)

      return
    }

    await saveDraftAndPublish()
  }

  // Modal handlers
  const handlePublishConfirm = async () => {
    setConfirmationModalOpen(false)

    // Validate again before publishing (same as Meli.tsx)
    if (!validateFormData({ skipDescriptionPrompt: true })) {
      return
    }

    await saveDraftAndPublish()
  }

  const handlePublishCancel = () => {
    setConfirmationModalOpen(false)
  }

  const handleShortDescriptionConfirm = () => {
    setShortDescriptionModalOpen(false)
    setConfirmationModalOpen(true)
  }

  const handleShortDescriptionCancel = () => {
    setShortDescriptionModalOpen(false)
  }

  // Sync internal form data state with external prop changes
  useEffect(() => {
    if (externalFormData) {
      setFormData(externalFormData)
    }
  }, [externalFormData])

  // Sync internal validation errors state with external prop changes
  useEffect(() => {
    if (externalValidationErrors) {
      setFormErrors(externalValidationErrors)
    }
  }, [externalValidationErrors])

  if (!userOrganization) {
    return <div>Loading...</div>
  }

  return (
    <div className="space-y-4">
      {/* Direct Platform Selection Tabs - No Accordion */}
      <div className="w-full rounded-md shadow-sm border border-gray-200">
        <Tabs.Root
          value={selectedPlatform}
          onValueChange={(value) => setSelectedPlatform(value as 'mercadolibre' | 'chileautos' | 'yapo')}
        >
          <Tabs.List className="flex border-b border-gray-200 bg-gray-50 rounded-t-md">
            <Tabs.Trigger
              value="mercadolibre"
              className="flex-1 py-4 px-6 text-sm font-medium text-gray-600 hover:text-gray-800 hover:bg-gray-100 data-[state=active]:text-blue-600 data-[state=active]:bg-white data-[state=active]:border-b-2 data-[state=active]:border-blue-500 transition-all duration-200 first:rounded-tl-md"
            >
              MercadoLibre
            </Tabs.Trigger>
            <Tabs.Trigger
              value="chileautos"
              className="flex-1 py-4 px-6 text-sm font-medium text-gray-600 hover:text-gray-800 hover:bg-gray-100 data-[state=active]:text-blue-600 data-[state=active]:bg-white data-[state=active]:border-b-2 data-[state=active]:border-blue-500 transition-all duration-200"
            >
              ChileAutos
            </Tabs.Trigger>
            <Tabs.Trigger
              value="yapo"
              className="flex-1 py-4 px-6 text-sm font-medium text-gray-600 hover:text-gray-800 hover:bg-gray-100 data-[state=active]:text-blue-600 data-[state=active]:bg-white data-[state=active]:border-b-2 data-[state=active]:border-blue-500 transition-all duration-200 last:rounded-tr-md"
            >
              Yapo
            </Tabs.Trigger>
          </Tabs.List>

          {/* MercadoLibre Content */}
          <Tabs.Content value="mercadolibre" className="p-4">
            {/* Nested Tab Navigation for MercadoLibre */}
            <div className="border-b border-gray-200 mb-4">
              <nav className="-mb-px flex space-x-8" aria-label="Tabs">
                <button
                  onClick={() => setActiveTab('details')}
                  className={`whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm ${
                    activeTab === 'details'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Detalles del Vehículo
                </button>
                <button
                  onClick={() => setActiveTab('images')}
                  className={`whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm ${
                    activeTab === 'images'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Imágenes Públicas ({publicImages.length})
                </button>
                <button
                  onClick={() => setActiveTab('video')}
                  className={`whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm ${
                    activeTab === 'video'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Video
                </button>
              </nav>
            </div>

            {/* Tab Content for MercadoLibre */}
            <div>
              {activeTab === 'details' && (
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold">Detalles del Vehículo - MercadoLibre</h3>

                  {/* Show loading message (same as vehicle detail page) */}
                  {(isDraftLoading || !minimumTimeElapsed || !externalApisComplete) &&
                    !(saveMessage && saveMessage.startsWith('✅')) && (
                      <div className="flex justify-center mt-2">
                        <div className="text-sm font-bold text-blue-600">📝 Cargando datos...</div>
                      </div>
                    )}

                  {/* Show form when data is loaded */}
                  {!isDraftLoading &&
                    minimumTimeElapsed &&
                    externalApisComplete &&
                    categoryAttrs &&
                    types &&
                    municipalities && (
                      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                        <InputField
                          label="Título"
                          name="title"
                          value={formData.title}
                          type="text"
                          handleInputChange={handleInputChange}
                          errors={formErrors}
                        />
                        <SelectField
                          label="Tipo"
                          name="type"
                          value={formData.type}
                          options={types || {}}
                          handleInputChange={handleInputChange}
                          errors={formErrors}
                        />
                        <InputField
                          label="Modelo"
                          name="model"
                          value={formData.model}
                          type="text"
                          handleInputChange={handleInputChange}
                          errors={formErrors}
                        />
                        <InputField
                          label="Versión"
                          name="version"
                          value={formData.version}
                          type="text"
                          handleInputChange={handleInputChange}
                          errors={formErrors}
                        />
                        <SelectField
                          label="Carrocería"
                          name="body"
                          value={formData.body}
                          options={categoryAttrs.bodies.reduce((acc, cur) => ({ ...acc, [cur]: cur }), {})}
                          handleInputChange={handleInputChange}
                          errors={formErrors}
                        />
                        <SelectField
                          label="Marca"
                          name="brand"
                          value={formData.brand}
                          options={categoryAttrs.brands
                            .filter((b: string) => b.length > 1 && !Number(b))
                            .reduce((acc, cur) => ({ ...acc, [cur]: cur }), {})}
                          handleInputChange={handleInputChange}
                          errors={formErrors}
                        />
                        <SelectField
                          label="Combustible"
                          name="fuel"
                          value={formData.fuel}
                          options={categoryAttrs.fuel.reduce((acc, cur) => ({ ...acc, [cur]: cur }), {})}
                          handleInputChange={handleInputChange}
                          errors={formErrors}
                        />
                        <SelectField
                          label="Transmisión"
                          name="gearbox"
                          value={formData.gearbox}
                          options={categoryAttrs.gearbox.reduce((acc, cur) => ({ ...acc, [cur]: cur }), {})}
                          handleInputChange={handleInputChange}
                          errors={formErrors}
                        />
                        <SelectField
                          label="Color"
                          name="color"
                          value={formData.color}
                          options={categoryAttrs.colors.reduce((acc, cur) => ({ ...acc, [cur]: cur }), {})}
                          handleInputChange={handleInputChange}
                          errors={formErrors}
                        />
                        <InputField
                          label="Nº de puertas"
                          name="doors"
                          value={formData.doors}
                          type="number"
                          handleInputChange={handleInputChange}
                          errors={formErrors}
                        />
                        <SelectField
                          label="Comuna"
                          name="municipality"
                          value={branchRegionId}
                          options={regions}
                          keyProp="regionId"
                          valueProp="name"
                          handleInputChange={handleRegionChange}
                          errors={formErrors}
                        />
                        <InputField
                          label="Calle y Nº"
                          name="street"
                          value={formData.street}
                          type="text"
                          handleInputChange={handleInputChange}
                          errors={formErrors}
                        />
                        <div className="sm:col-span-2 lg:col-span-3">
                          <InputTextArea
                            label="Descripción"
                            name="comments"
                            value={formData.comments}
                            handleInputChange={handleInputChange}
                            errors={formErrors}
                            minLength={MIN_DESCRIPTION_LENGTH}
                            showCounter={true}
                            placeholder={
                              !formData.comments && userData?.organization?.external?.meli?.defaultDescription
                                ? userData?.organization?.external?.meli?.defaultDescription
                                : undefined
                            }
                          />
                        </div>
                      </div>
                    )}
                </div>
              )}

              {activeTab === 'images' && (
                <div className="space-y-4">
                  {/* Bulk Selection Toolbar */}
                  {publicImages.length > 0 && (
                    <ImageSelectionToolbar
                      isSelectionMode={imageSelection.isSelectionMode}
                      selectedCount={imageSelection.selectedCount}
                      totalCount={imageSelection.totalCount}
                      onToggleSelectionMode={imageSelection.toggleSelectionMode}
                      onSelectAll={imageSelection.selectAll}
                      onSelectNone={imageSelection.selectNone}
                      onBulkDownload={imageSelection.handleBulkDownload}
                      canDownload={imageSelection.canDownload}
                      downloadProgress={downloadProgress}
                      onBulkDelete={imageSelection.handleBulkDelete}
                      canDelete={imageSelection.canDelete}
                      showDeleteOption={true}
                      deleteProgress={deleteProgress}
                    />
                  )}

                  <div className="flex justify-between items-center">
                    <h3 className="text-lg font-semibold">Imágenes Públicas - MercadoLibre</h3>
                    {!imageSelection.isSelectionMode && (
                      <div className="flex items-center gap-4">
                        <label
                          htmlFor="file-upload-tab"
                          className={`cursor-pointer rounded bg-green-500 px-4 py-2 text-white hover:bg-green-600 ${isUploading && 'cursor-not-allowed opacity-50'}`}
                        >
                          {isUploading ? 'Cargando...' : 'Agregar imagen...'}
                        </label>
                        <input
                          id="file-upload-tab"
                          type="file"
                          multiple={true}
                          accept="image/*"
                          onChange={handleFileChange}
                          className="hidden"
                        />
                        <Button onClick={handleSaveOrder} disabled={isOrderSaveDisabled}>
                          Guardar orden
                        </Button>
                      </div>
                    )}
                  </div>

                  {/* Enhanced Grid Layout with Lightbox Integration */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 max-h-[600px] overflow-y-auto">
                    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                      <SortableContext items={publicImages} strategy={verticalListSortingStrategy}>
                        {publicImages.map((id, index) => (
                          <div key={id} className="relative">
                            <SortableItem
                              id={id}
                              selectionMode={imageSelection.isSelectionMode}
                              isSelected={imageSelection.isImageSelected(id)}
                              onSelectionChange={imageSelection.handleSelectionChange}
                              onImageClick={() => openImageInLightbox(index)}
                            />
                          </div>
                        ))}
                      </SortableContext>
                    </DndContext>
                  </div>
                  {publicImages.length === 0 && (
                    <div className="text-center py-12 text-gray-500">
                      <p>No hay imágenes públicas. Agregue algunas para mejorar la publicación.</p>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'video' && (
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold">Video - MercadoLibre</h3>
                  <VehicleVideoTab
                    vehicle={vehicle}
                    canModify={true}
                    onRefresh={onVideoUpload ? () => onVideoUpload(vehicle._id) : onPublishSuccess}
                  />
                </div>
              )}
            </div>

            {/* Save message area (same as vehicle detail page) */}
            {(saveMessage || saveError) && (
              <div className="flex justify-center mt-2">
                <div
                  className={`text-sm ${saveMessage && saveMessage.startsWith('✅') ? 'text-green-600' : 'text-red-600'}`}
                >
                  {saveMessage || saveError}
                </div>
              </div>
            )}

            {/* Notification display */}
            {publishMessage && (
              <div className="flex justify-center mt-4">
                <Info
                  message={publishMessage}
                  trigger={publishTrigger}
                  onTimeout={() => setPublishMessage('')}
                  noCenter={true}
                />
              </div>
            )}

            {/* Action Buttons for MercadoLibre */}
            <div className="mt-6 pt-4 border-t border-gray-200 flex justify-center gap-4">
              <Button
                onClick={handleSaveDraft}
                disabled={savingLoading}
                className="bg-gray-600 hover:bg-gray-700 text-white font-medium py-2 px-4 rounded-md transition-colors duration-200"
              >
                {savingLoading ? 'Guardando...' : 'Guardar Borrador'}
              </Button>
              <Button
                onClick={() => beforePublish()}
                disabled={isPublishing}
                className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-md transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isPublishing
                  ? isPublishedOnMeli
                    ? 'Republicando...'
                    : 'Publicando...'
                  : isPublishedOnMeli
                    ? 'Republicar en MercadoLibre'
                    : 'Publicar en MercadoLibre'}
              </Button>
            </div>
          </Tabs.Content>

          {/* ChileAutos Content */}
          <Tabs.Content value="chileautos" className="p-4">
            <ChileAutosForm
              vehicle={vehicle}
              branch={branch}
              onPublishSuccess={onPublishSuccess}
              onImageUpload={onImageUpload}
            />
          </Tabs.Content>

          {/* Yapo Content */}
          <Tabs.Content value="yapo" className="p-4">
            <YapoForm
              vehicle={vehicle}
              branch={branch}
              onPublishSuccess={onPublishSuccess}
              onImageUpload={onImageUpload}
            />
          </Tabs.Content>
        </Tabs.Root>
      </div>

      {/* Confirmation Modals */}
      {confirmationModalOpen && (
        <ConfirmationModal
          message={
            isPublishedOnMeli
              ? 'Este vehículo ya está publicado en MercadoLibre. Al continuar se sobrescribirá el aviso que está en línea con los datos de este formulario. ¿Desea republicarlo?'
              : '¿Está seguro de que desea publicar este vehículo en MercadoLibre?'
          }
          onConfirm={handlePublishConfirm}
          onCancel={handlePublishCancel}
        />
      )}

      {shortDescriptionModalOpen && (
        <ConfirmationModal
          message={`La descripción debe tener al menos ${MIN_DESCRIPTION_LENGTH} caracteres para una buena publicación. Actualmente tiene ${(formData.comments ?? '').trim().length} caracteres. ¿Desea publicar de todos modos?`}
          onConfirm={handleShortDescriptionConfirm}
          onCancel={handleShortDescriptionCancel}
        />
      )}

      {/* Bulk Delete Confirmation Modal */}
      {deleteConfirmationOpen && (
        <ConfirmationModal
          message={`¿Estás seguro de eliminar ${imagesToDelete.length} imagen${imagesToDelete.length > 1 ? 'es' : ''}? Esta acción no se puede deshacer.`}
          onConfirm={handleBulkDeleteConfirm}
          onCancel={handleBulkDeleteCancel}
        />
      )}

      {/* Enhanced Image Lightbox */}
      <ImageLightbox
        isOpen={isLightboxOpen}
        onClose={closeLightbox}
        imageUrls={publicImages}
        initialIndex={lightboxInitialIndex}
        onDelete={(imageUrl) => {
          // Remove from local state immediately
          setPublicImages((prevImages) => prevImages.filter((url) => url !== imageUrl))

          // Trigger data refresh
          if (onImageUpload) {
            onImageUpload(vehicle._id)
          }
        }}
        canDelete={true}
      />
    </div>
  )
}

export default PublicationFormManager
